import { FormEvent, useState } from "react";
import AdminResourceForm, { AdminField } from "../../components/admin/AdminResourceForm";
import { useAdminData } from "../../admin/AdminDataContext";
import { Testimonial } from "../../types";

const emptyTestimonial = {
  clientName: "",
  quote: "",
  eventType: "",
  rating: "5",
};

function testimonialToFields(testimonial: typeof emptyTestimonial): AdminField[] {
  return [
    { name: "clientName", label: "Client name", value: testimonial.clientName, required: true },
    { name: "quote", label: "Quote", value: testimonial.quote, required: true },
    { name: "eventType", label: "Event type", value: testimonial.eventType },
    { name: "rating", label: "Rating (1–5)", value: testimonial.rating },
  ];
}

function fieldsToTestimonial(fields: AdminField[]): Omit<Testimonial, "id"> {
  const map = Object.fromEntries(fields.map((f) => [f.name, f.value]));
  const rating = map.rating ? Number(map.rating) : undefined;
  return {
    clientName: map.clientName,
    quote: map.quote,
    eventType: map.eventType || undefined,
    rating: rating && rating >= 1 && rating <= 5 ? rating : undefined,
  };
}

function AdminTestimonials() {
  const { testimonials, loading, error, addTestimonial, updateTestimonial, removeTestimonial } =
    useAdminData();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fields, setFields] = useState<AdminField[]>(testimonialToFields(emptyTestimonial));

  const openCreate = () => {
    setEditingId(null);
    setFields(testimonialToFields(emptyTestimonial));
    setShowForm(true);
  };

  const openEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id);
    setFields(
      testimonialToFields({
        clientName: testimonial.clientName,
        quote: testimonial.quote,
        eventType: testimonial.eventType ?? "",
        rating: testimonial.rating?.toString() ?? "5",
      })
    );
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleChange = (name: string, value: string) => {
    setFields((prev) => prev.map((f) => (f.name === name ? { ...f, value } : f)));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = fieldsToTestimonial(fields);
    if (!payload.clientName || !payload.quote) return;
    try {
      if (editingId) {
        await updateTestimonial(editingId, payload);
      } else {
        await addTestimonial(payload);
      }
      closeForm();
    } catch {
      // mutation error surfaced via AdminDataContext if needed
    }
  };

  const handleDelete = async (testimonial: Testimonial) => {
    if (window.confirm(`Delete testimonial from "${testimonial.clientName}"?`)) {
      await removeTestimonial(testimonial.id);
    }
  };

  if (loading) return <p className="body-text">Loading testimonials...</p>;
  if (error) return <p className="admin-error">Error: {error}</p>;

  return (
    <div className="admin-card">
      <div className="admin-card__header">
        <h1 className="h1-text">Testimonials</h1>
        {!showForm && (
          <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>
            Add testimonial
          </button>
        )}
      </div>

      {showForm && (
        <AdminResourceForm
          title={editingId ? "Edit testimonial" : "Add testimonial"}
          fields={fields}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
          submitLabel={editingId ? "Save changes" : "Add testimonial"}
        />
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Event type</th>
            <th>Rating</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {testimonials.map((testimonial) => (
            <tr key={testimonial.id}>
              <td>{testimonial.clientName}</td>
              <td>{testimonial.eventType ?? "—"}</td>
              <td>{testimonial.rating ?? "—"}</td>
              <td>
                <div className="admin-table-actions">
                  <button type="button" className="admin-btn" onClick={() => openEdit(testimonial)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger"
                    onClick={() => handleDelete(testimonial)}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminTestimonials;
